import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  HorizontalBars,
  KpiCard,
  NoPermissionState,
  PageHeader,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Award, Cake, HeartHandshake, ShieldCheck, UserRoundX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { useCan, useHasModule } from '@/features/auth/session';
import { crmOverviewQueryOptions } from '@/features/crm/queries';
import { moduleColor, moduleLabel } from '@/features/insights/labels';
import { validateInsightsSearch, type InsightsSearch } from '@/features/insights/search';
import { useInsightsPeriod } from '@/features/insights/useInsightsPeriod';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatNumber } from '@/lib/format';
import { useScopedPropertyId } from '@/lib/property';

export const Route = createFileRoute('/_app/crm/')({
  validateSearch: validateInsightsSearch,
  component: CrmOverviewPage,
});

/** Vue d'ensemble de la clientèle (ADR-025) : qui vient, qui revient, qui dépense. */
function CrmOverviewPage() {
  const { t, i18n } = useTranslation(['crm', 'insights', 'common', 'errors']);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canRead = useCan('crm.read');
  const hasPos = useHasModule('pos');
  const propertyId = useScopedPropertyId();
  const { preset, custom, range } = useInsightsPeriod(search);
  const setSearch = (next: Partial<InsightsSearch>) =>
    void navigate({ search: (current) => ({ ...current, ...next }), replace: true });
  const query = useQuery(crmOverviewQueryOptions(range, propertyId, canRead));
  const data = query.data;
  const currency = data?.currencyCode ?? 'XOF';
  const money = (value: number) => formatAmount(value, i18n.language, currency);
  const number = (value: number) => formatNumber(value, i18n.language);

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={t('crm:overview.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionBody')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('crm:overview.title')}
        subtitle={t('crm:overview.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('crm:title') },
          { label: t('crm:overview.title') },
        ]}
      />
      <div className="stack">
        <Card>
          <CardBody>
            <PeriodPicker
              preset={preset}
              onPresetChange={(value) => setSearch({ period: value })}
              range={range}
              custom={custom}
              onCustomChange={(value) =>
                setSearch({ period: 'CUSTOM', from: value.from, to: value.to })
              }
              hideCompare
            />
          </CardBody>
        </Card>

        {query.isError ? (
          <ErrorState
            title={t('errors:generic.title')}
            description={errorMessage(query.error, t)}
            traceId={errorTraceId(query.error)}
            retryLabel={t('common:actions.retry')}
            onRetry={() => void query.refetch()}
            traceCopyLabel={t('common:trace.copy')}
            traceCopiedLabel={t('common:trace.copied')}
          />
        ) : (
          <>
            <div className="grid grid-4">
              <KpiCard
                label={t('crm:kpi.known')}
                value={data ? number(data.customers.known) : '—'}
                loading={query.isPending}
                tone="violet"
              />
              <KpiCard
                label={t('crm:kpi.active')}
                value={data ? number(data.customers.active) : '—'}
                footer={
                  data
                    ? `${t('crm:kpi.new')} : ${number(data.customers.new)} · ${t('crm:kpi.returning')} : ${number(data.customers.returning)}`
                    : undefined
                }
                loading={query.isPending}
                tone="green"
              />
              <KpiCard
                label={t('crm:kpi.identifiedShare')}
                value={
                  data
                    ? data.customers.identifiedOrdersShare === null
                      ? '—'
                      : `${number(data.customers.identifiedOrdersShare)} %`
                    : '—'
                }
                footer={hasPos ? t('crm:kpi.identifiedShareFoot') : undefined}
                loading={query.isPending}
                tone="blue"
              />
              <KpiCard
                label={t('crm:kpi.members')}
                value={data?.loyalty ? number(data.loyalty.members) : '—'}
                footer={
                  data?.loyalty
                    ? `${t('crm:kpi.earned')} : ${number(data.loyalty.earned)} · ${t('crm:kpi.redeemed')} : ${number(data.loyalty.redeemed)}`
                    : t('crm:loyalty.noProgram')
                }
                loading={query.isPending}
                tone="amber"
              />
            </div>

            <div className="grid grid-2">
              <Card>
                <CardHeader
                  title={t('crm:sections.spendByModule')}
                  icon={<HeartHandshake className="ico ico-16" aria-hidden="true" />}
                  iconTone="green"
                />
                <CardBody>
                  {query.isPending || !data ? (
                    <Skeleton height={120} />
                  ) : data.spendByModule.length === 0 ? (
                    <EmptyState title={t('crm:detail.noSpend')} />
                  ) : (
                    <HorizontalBars
                      items={data.spendByModule.map((row) => ({
                        key: row.module,
                        label: moduleLabel(t, row.module),
                        value: row.amount,
                        display: money(row.amount),
                        color: moduleColor(row.module),
                      }))}
                    />
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardHeader title={t('crm:sections.topCustomers')} />
                <CardBody density="flush">
                  {query.isPending || !data ? (
                    <div className="p-4">
                      <Skeleton height={120} />
                    </div>
                  ) : data.topCustomers.length === 0 ? (
                    <EmptyState title={t('crm:customers.empty')} />
                  ) : (
                    <TableWrap>
                      <Table compact>
                        <caption className="sr-only">{t('crm:sections.topCustomers')}</caption>
                        <thead>
                          <tr>
                            <Th>{t('crm:customers.columns.customer')}</Th>
                            <Th align="right">{t('crm:customers.columns.visits')}</Th>
                            <Th align="right">{t('crm:customers.columns.spend')}</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.topCustomers.map((row) => (
                            <Tr key={row.id}>
                              <Td>
                                <Link
                                  className="link-quiet"
                                  to="/crm/customers/$customerId"
                                  params={{ customerId: row.id }}
                                >
                                  {row.displayName}
                                </Link>
                              </Td>
                              <Td align="right" numeric>
                                {number(row.visits)}
                              </Td>
                              <Td align="right" numeric>
                                {money(row.spend)}
                              </Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  )}
                </CardBody>
              </Card>
            </div>

            <div className="grid grid-3">
              <Card>
                <CardHeader
                  title={t('crm:sections.followUps')}
                  icon={<Cake className="ico ico-16" aria-hidden="true" />}
                  iconTone="amber"
                  actions={
                    <Link
                      className="link-quiet t-13"
                      to="/crm/follow-ups"
                      search={{ kind: 'BIRTHDAYS' }}
                    >
                      {t('crm:followUps.title')}
                    </Link>
                  }
                />
                <CardBody>
                  {query.isPending || !data ? (
                    <Skeleton height={80} />
                  ) : (
                    <div className="kv-grid">
                      <div>
                        <div className="kv__k">{t('crm:kpi.birthdays')}</div>
                        <div className="kv__v t-18 strong">{number(data.birthdays.count)}</div>
                        <div className="muted t-12">
                          {t('crm:kpi.birthdaysFoot', { count: data.birthdays.windowDays })}
                        </div>
                      </div>
                      <div>
                        <div className="kv__k">
                          <UserRoundX className="ico ico-14" aria-hidden="true" />{' '}
                          {t('crm:kpi.inactive')}
                        </div>
                        <div className="kv__v t-18 strong">{number(data.inactive.count)}</div>
                        <div className="muted t-12">
                          {t('crm:kpi.inactiveFoot', { count: data.inactive.afterDays })}
                        </div>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardHeader
                  title={t('crm:sections.consents')}
                  icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
                  iconTone="blue"
                />
                <CardBody>
                  {query.isPending || !data ? (
                    <Skeleton height={80} />
                  ) : (
                    <HorizontalBars
                      items={data.consents.map((row) => ({
                        key: row.channel,
                        label: t(`crm:consents.channels.${row.channel}`),
                        value: row.granted,
                        display: number(row.granted),
                      }))}
                    />
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardHeader
                  title={t('crm:sections.loyalty')}
                  icon={<Award className="ico ico-16" aria-hidden="true" />}
                  iconTone="amber"
                  actions={
                    <Link className="link-quiet t-13" to="/crm/loyalty">
                      {t('crm:loyalty.program')}
                    </Link>
                  }
                />
                <CardBody>
                  {query.isPending || !data ? (
                    <Skeleton height={80} />
                  ) : !data.loyalty ? (
                    <EmptyState title={t('crm:loyalty.noProgram')} />
                  ) : (
                    <div className="stack stack--sm">
                      <div className="row row--wrap" style={{ gap: 6 }}>
                        <span className="t-13 strong">{data.loyalty.programName}</span>
                        <Badge tone={data.loyalty.isActive ? 'green' : 'neutral'}>
                          {data.loyalty.isActive
                            ? t('crm:segments.active')
                            : t('crm:loyalty.inactiveNotice')}
                        </Badge>
                      </div>
                      <HorizontalBars
                        items={data.loyalty.byTier.map((row) => ({
                          key: row.code,
                          label: row.name,
                          value: row.count,
                          display: number(row.count),
                        }))}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </>
        )}
      </div>
    </Container>
  );
}
